import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

// T-231 — Promemoria pulizie. Le spunte si resettano a ogni finestra
// (giornaliera/settimanale/mensile) by-design (eventi cleaning_done
// append-only) e NON si può flaggare retroattivamente: se il cliente
// dimentica, il registro esportato mostra un buco. Questo modulo schedula
// una LocalNotification quando ci sono voci non ancora registrate.
//
// Solo nativo (Android/iOS): su web/PWA LocalNotifications non è affidabile
// (nessun delivery garantito a app chiusa) → return silenzioso.

/** Notifica one-shot "hai N pulizie pendenti" — ricreata a ogni chiamata. */
const PENDING_NOTIFICATION_ID = 777001;
/** Safe-net ricorrente giornaliera (18:00) — creata una volta sola. */
const DAILY_SAFETY_NET_ID = 777002;
/** T-341: alert "oggi tocca il cassetto settimanale/mensile" (one-shot al nextDue). */
const WEEKLY_DUE_ID = 777003;
const MONTHLY_DUE_ID = 777004;
const DAILY_GUARD_KEY = 'trackeat.cleaning_reminder_daily_v1';

async function ensurePermission(): Promise<boolean> {
  const cur = await LocalNotifications.checkPermissions();
  if (cur.display === 'granted') return true;
  const req = await LocalNotifications.requestPermissions();
  return req.display === 'granted';
}

// Oggi alle 18:00 se l'ora corrente è < 18, altrimenti domani alle 09:00.
function nextReminderAt(now: Date): Date {
  const fireAt = new Date(now);
  if (now.getHours() < 18) {
    fireAt.setHours(18, 0, 0, 0);
    return fireAt;
  }
  fireAt.setDate(fireAt.getDate() + 1);
  fireAt.setHours(9, 0, 0, 0);
  return fireAt;
}

// Safe-net: una notifica ricorrente ogni giorno alle 18:00, indipendente
// dallo stato pendenze (copre il caso "app mai aperta oggi"). Schedulata
// UNA volta sola per installazione (guard in localStorage).
async function ensureDailySafetyNet(): Promise<void> {
  try {
    if (localStorage.getItem(DAILY_GUARD_KEY) !== null) return;
  } catch {
    // localStorage indisponibile: meglio uscire che rischiare schedule duplicati.
    return;
  }
  await LocalNotifications.schedule({
    notifications: [
      {
        id: DAILY_SAFETY_NET_ID,
        title: 'TrackEAT — Controlli HACCP',
        body: 'Ricorda di registrare pulizie e temperature di oggi.',
        schedule: { on: { hour: 18, minute: 0 }, repeats: true, allowWhileIdle: true },
      },
    ],
  });
  try {
    localStorage.setItem(DAILY_GUARD_KEY, new Date().toISOString());
  } catch {
    // ignore — al peggio la safe-net verrà ri-schedulata (stesso id → sovrascrive)
  }
}

/**
 * Schedula (o cancella) il promemoria per le pulizie non ancora registrate
 * nella finestra corrente. Best-effort: non lancia MAI verso il chiamante.
 *
 * - pendingCount > 0 → notifica id 777001 per oggi 18:00 (o domani 09:00
 *   se sono già passate le 18), sempre dopo cancel preventivo dello stesso id
 *   così il conteggio nel testo resta aggiornato.
 * - pendingCount === 0 → solo cancel (niente notifica).
 */
export async function scheduleCleaningReminder(opts: { pendingCount: number }): Promise<void> {
  try {
    if (!Capacitor.isNativePlatform()) return;
    if (!(await ensurePermission())) return;

    // Cancel preventivo: lo stato delle pendenze può essere cambiato
    // dall'ultima schedulazione (voci flaggate nel frattempo).
    try {
      await LocalNotifications.cancel({ notifications: [{ id: PENDING_NOTIFICATION_ID }] });
    } catch {
      // nessuna notifica con quell'id — ok
    }

    if (opts.pendingCount > 0) {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: PENDING_NOTIFICATION_ID,
            title: 'TrackEAT — Pulizie da registrare',
            body: `Hai ${opts.pendingCount} pulizie non ancora registrate per la finestra corrente. Registrale per non lasciare buchi nel registro HACCP (non è possibile farlo retroattivamente).`,
            schedule: { at: nextReminderAt(new Date()), allowWhileIdle: true },
          },
        ],
      });
    }

    await ensureDailySafetyNet();
  } catch {
    // Il promemoria è best-effort: mai propagare errori alla UI chiamante.
  }
}

/**
 * T-341 — Alert dei cassetti pulizie SETTIMANALE/MENSILE nel giorno previsto.
 *
 * Ogni device della sede sincronizza la STESSA config (giorno settimana/mese su
 * `businesses`) → ognuno schedula la stessa notifica locale → l'alert compare su
 * TUTTI i dispositivi dell'azienda, senza push server (offline-first).
 *
 * One-shot al `nextDue` (alle 09:00, calcolato a monte da cassettoSchedule):
 * cancel+reschedule a ogni chiamata. `null` → solo cancel (nessuna voce nel
 * cassetto / non configurato). Best-effort: non lancia mai.
 */
export async function scheduleCassettoDueReminders(opts: {
  weeklyAt: Date | null;
  monthlyAt: Date | null;
}): Promise<void> {
  try {
    if (!Capacitor.isNativePlatform()) return;
    if (!(await ensurePermission())) return;

    const plan: { id: number; at: Date | null; label: string }[] = [
      { id: WEEKLY_DUE_ID, at: opts.weeklyAt, label: 'settimanali' },
      { id: MONTHLY_DUE_ID, at: opts.monthlyAt, label: 'mensili' },
    ];

    for (const { id, at, label } of plan) {
      try {
        await LocalNotifications.cancel({ notifications: [{ id }] });
      } catch {
        // nessuna notifica con quell'id — ok
      }
      if (at && at.getTime() > Date.now()) {
        await LocalNotifications.schedule({
          notifications: [
            {
              id,
              title: 'TrackEAT — Pulizie programmate',
              body: `Oggi vanno eseguite e registrate le pulizie ${label}. Apri TrackEAT per spuntarle.`,
              schedule: { at, allowWhileIdle: true },
            },
          ],
        });
      }
    }
  } catch {
    // best-effort
  }
}
